import { all, call, fork, put, takeLeading } from "typed-redux-saga";
import { Action, productDetailsActions } from "./product-details-store";
import { errorActions as errorActions } from "src/components/error-viewer/error-store";
import { Model_ProductPictureGet, ProductPictureService, ProductService, Shared_PictureResolutions } from "src/backend/openapi/v1";
import { takeLeadingPerKey } from "src/utils";
import { SinglePicture } from "./product-details-types";
import { getCache, saveCache } from "src/utils/cache-utils";
import { takeLatestPerKey } from "@itk/typescript-utils";
import { addEditConfigurationDialogActions } from "../configurator/components/add-edit-configuration-dialog/add-edit-configuration-dialog-store";
import { CACHE_KEYS } from "src/utils/constants";

export function* getProductByProductId(action: ReturnType<typeof productDetailsActions.getProductByProductIdRequest>) {
  try {
    const result = yield* call(ProductService.getApiV1Products, action.payload.productId, action.payload.language);
    yield put(productDetailsActions.getProductByProductIdSuccess(result));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(productDetailsActions.getProductByProductIdFail());
  }
}

export function* watchgetProductByProductId() {
  yield takeLeading(Action.GetProductByProductIdRequest, getProductByProductId);
}

export function* getProductPictures(action: ReturnType<typeof productDetailsActions.getProductPicturesRequest>) {
  try {
    const { productId, resolution, callBack, productPictureIds } = action.payload;

    const productPictresIdsKey = productPictureIds?.join("_") ?? "none";
    const cacheKey = CACHE_KEYS.ALL_PRODUCT_PICTURES(productId, resolution, productPictresIdsKey);

    const cachedResponse = yield* call(() => getCache(cacheKey));

    if (cachedResponse) {
      const json = yield* call(() => cachedResponse.json() as unknown as Model_ProductPictureGet[]);
      callBack(true, json);
      return;
    }

    const productPictures: Model_ProductPictureGet[] = yield* call(ProductPictureService.getApiV1ProductPictures, resolution, productId);
    if (productPictures.length > 0) {
      yield* call(() => saveCache(cacheKey, productPictures));
    }
    callBack(true, productPictures);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  }
}

export function* watchGetProductPictures() {
  yield takeLeading(Action.GetProductPicturesRequest, getProductPictures);
}

export function* getSingleProductPicture(action: ReturnType<typeof productDetailsActions.getSingleProductPictureRequest>) {
  try {
    let productPicture: SinglePicture;
    const cacheKey = CACHE_KEYS.PRODUCT_PICTURE(action.payload.pictureId);

    const cachedResponse = yield* call(() => getCache(cacheKey));

    if (cachedResponse) {
      productPicture = yield* call(() => cachedResponse.json() as unknown as SinglePicture);
    } else {
      const result = yield* call(ProductPictureService.getApiV1ProductPictures1, action.payload.pictureId, action.payload.resolution);
      productPicture = { ...result, resolution: action.payload.resolution };
      yield* call(() =>
        saveCache(cacheKey, {
          picture: productPicture.picture,
          productPictureId: productPicture.productPictureId,
          productId: productPicture.productId,
          resolution: action.payload.resolution
        } as SinglePicture)
      );
    }

    action.payload.callBack(true, productPicture);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  }
}

export function* watchGetSingleProductPicture() {
  yield takeLeadingPerKey(Action.getSingleProductPictureRequest, getSingleProductPicture, action => action.payload.pictureId);
}

export function* getMultipleProductPictures(action: ReturnType<typeof productDetailsActions.getMultipleProductPicturesRequest>) {
  try {
    const productPictures: SinglePicture[] = [];
    const remainingPictures: string[] = [];

    // Get pictures from cache if it exists
    for (const productPictureId of action.payload.productPictureIds) {
      const cacheKey = CACHE_KEYS.PRODUCT_PICTURE(productPictureId);

      const cachedResponse = yield* call(() => getCache(cacheKey));
      if (cachedResponse) {
        const data = yield* call(() => cachedResponse.json() as unknown as SinglePicture);
        const resolutionHierarchy = [Shared_PictureResolutions.Small, Shared_PictureResolutions.Medium, Shared_PictureResolutions.Large];
        const requestedResolution = resolutionHierarchy.indexOf(action.payload.resolution);
        const cachedResolution = resolutionHierarchy.indexOf(data.resolution);

        if (requestedResolution > cachedResolution) {
          remainingPictures.push(productPictureId);
        } else {
          productPictures.push(data);
        }
      } else remainingPictures.push(productPictureId);
    }

    // Fetch pictures which arent in cache
    if (remainingPictures.length > 0) {
      const pictures = yield* call(ProductPictureService.postApiV1ProductPicturesMultiple, remainingPictures, action.payload.resolution);

      // store result in cache
      for (const picture of pictures) {
        const cacheKey = CACHE_KEYS.PRODUCT_PICTURE(picture.productPictureId);
        const singlePicture: SinglePicture = { picture: picture.picture, productId: picture.productId, productPictureId: picture.productPictureId, resolution: action.payload.resolution };
        yield* call(() => saveCache(cacheKey, singlePicture));
        productPictures.push(singlePicture);
      }
    }
    yield put(addEditConfigurationDialogActions.updateProductPictures(productPictures));

    action.payload.callBack(true, productPictures);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  }
}

export function* watchGetMultipleProductPictures() {
  yield takeLatestPerKey(Action.getMultipleProductPicturesRequest, getMultipleProductPictures, action => action.payload.productPictureIds.join(","));
}

export function* productDetailsSaga() {
  yield all([fork(watchgetProductByProductId), fork(watchGetMultipleProductPictures), fork(watchGetProductPictures), fork(watchGetSingleProductPicture)]);
}
